/* eslint-disable lit/binding-positions, lit/no-invalid-html  */
import { html } from 'lit/static-html.js';
import { LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { type ColorToken, toCSSValue } from '@loadsmart/miranda-tokens';

import { PolymorphicTagMixin } from '../../mixins/polymorphic-tag';
import type { TypographyVariant } from '../../styles/typography';
import type { HTMLTag } from '../../mixins/polymorphic-tag';

import styles from './text.styles';

export interface TextProps {
	variant?: TypographyVariant;
	color?: ColorToken;
	as?: HTMLTag;
}

export class Text extends PolymorphicTagMixin(
	LitElement,
	['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span'],
	'span',
) {
	static override styles = [styles()];

	static override get properties() {
		return {
			variant: { type: String, reflect: true },
			color: { type: String },
		};
	}

	/**
	 * Typography variant.
	 * @type {TypographyVariant}
	 */
	declare variant: TypographyVariant;

	/**
	 * Text color.
	 * @type {ColorToken}
	 */
	declare color: ColorToken;

	constructor() {
		super();

		this.as = 'span';
		this.variant = 'body-md';
		this.color = 'color-text-primary';
	}

	override render() {
		return html`
			<${this.tag}
				class=${classMap({
					'm-text': true,
					'-body-lg-bold': this.variant === 'body-lg-bold',
					'-body-lg': this.variant === 'body-lg',
					'-body-md-bold-underline': this.variant === 'body-md-bold-underline',
					'-body-md-bold': this.variant === 'body-md-bold',
					'-body-md-underline': this.variant === 'body-md-underline',
					'-body-md': this.variant === 'body-md',
					'-body-sm-bold-underline': this.variant === 'body-sm-bold-underline',
					'-body-sm-bold': this.variant === 'body-sm-bold',
					'-body-sm-underline': this.variant === 'body-sm-underline',
					'-body-sm': this.variant === 'body-sm',
					'-button-lg': this.variant === 'button-lg',
					'-button-md': this.variant === 'button-md',
					'-button-sm': this.variant === 'button-sm',
					'-chips-md': this.variant === 'chips-md',
					'-chips-sm': this.variant === 'chips-sm',
					'-heading-lg-bold': this.variant === 'heading-lg-bold',
					'-heading-lg': this.variant === 'heading-lg',
					'-heading-md-bold': this.variant === 'heading-md-bold',
					'-heading-md': this.variant === 'heading-md',
					'-heading-sm-bold': this.variant === 'heading-sm-bold',
					'-heading-sm': this.variant === 'heading-sm',
					'-hero-lg-bold': this.variant === 'hero-lg-bold',
					'-hero-lg': this.variant === 'hero-lg',
					'-hero-md-bold': this.variant === 'hero-md-bold',
					'-hero-md': this.variant === 'hero-md',
					'-hero-sm-bold': this.variant === 'hero-sm-bold',
					'-hero-sm': this.variant === 'hero-sm',
					'-number-md': this.variant === 'number-md',
					'-number-sm': this.variant === 'number-sm',
					'-table-header-md': this.variant === 'table-header-md',
					'-table-header-sm': this.variant === 'table-header-sm',
				})}
				style=${styleMap({
					// FIXME: how to set the color without being vulnerable to unsafe CSS?
					'--m-text-color': toCSSValue(this.color),
				})}
			>
				<slot></slot>
			</${this.tag}>
		`;
	}
}

window.customElements.define('m-text', Text);

declare global {
	interface HTMLElementTagNameMap {
		'm-text': Text;
	}
}
